import React, {useRef, useState} from "react";
import {TAP_TOLERANCE_PX} from "./useNavLayout";

/** The axis a container slides along: "x" for the side panel, "y" for the sheet. */
export type SlideAxis = "x" | "y";

/**
 * How much of the way a drag has to travel before releasing it flips the container. Short of this it
 * springs back to the end it started from, so a half-hearted swipe does not change anything.
 */
const COMMIT_TRAVEL_FRACTION = 1 / 3;

/** A gesture that never turned into a drag, left for the caller to interpret. */
export interface SlideTap {
    startedOpen: boolean;
    /** Whether the gesture started on a button, rather than on empty space in the container. */
    fromControl: boolean;
}

export interface SlideGestureOptions {
    axis: SlideAxis;
    /** Offset the container rests at when closed. Open is always 0. */
    closedOffset: number;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    /** Leaves a gesture that runs mostly along the other axis to whatever is scrolling inside. */
    yieldsToCrossAxisScroll?: boolean;
    /** Swallows the click a drag ends on, so a swipe across a row does not also activate it. */
    swallowClickAfterDrag?: boolean;
    /** What a tap does. Left to whatever was tapped when this is not given. */
    onTap?: (tap: SlideTap) => void;
}

export interface SlideGesture {
    /** How far the container is currently pushed towards closed, in pixels. */
    offset: number;
    /** True while the pointer is driving the offset, when the transform must not be animated. */
    isDragging: boolean;
    handlers: {
        onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
        onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
        onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
        onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
        onClickCapture?: (event: React.MouseEvent<HTMLElement>) => void;
    };
}

interface DragState {
    /** Where the pointer went down, in the two axes the offset does and does not follow. */
    startAlong: number;
    startAcross: number;
    startedOpen: boolean;
    moved: boolean;
    /** Set once a gesture turns out to belong to whatever is scrolling inside. */
    abandoned: boolean;
    fromControl: boolean;
}

/**
 * Drag-to-slide for a container that rests either open or closed: follows the pointer while it moves and
 * settles to whichever end the gesture was heading for.
 *
 * Hand-rolled from pointer events rather than taken from MUI's SwipeableDrawer, which renders into a modal
 * with a backdrop that leaves the panorama and the view controls dead while it is open, and only implements
 * its swipe for touch events. Pointer events cover mouse, touch and pen from one code path.
 */
export function useSlideGesture(options: SlideGestureOptions): SlideGesture {
    const {axis, closedOffset, isOpen, setIsOpen, yieldsToCrossAxisScroll, swallowClickAfterDrag,
        onTap} = options;

    // How far the container is pushed towards closed. Null unless a drag is in progress.
    const [dragOffset, setDragOffset] = useState<number | null>(null);
    const dragRef = useRef<DragState | null>(null);

    // A drag ends in a click on whatever it started from, which would otherwise follow the row it was
    // swiped across.
    const wasDragged = useRef(false);

    // The offset runs from 0 when open to closedOffset when closed, and both axes are measured from the
    // top left, so travel along the axis is positive in the direction that closes the container: right
    // for the panel, down for the sheet.
    const along = (event: React.PointerEvent<HTMLElement>) => axis === "x" ? event.clientX : event.clientY;
    const across = (event: React.PointerEvent<HTMLElement>) => axis === "x" ? event.clientY : event.clientX;

    const commitDistance = closedOffset * COMMIT_TRAVEL_FRACTION;

    const handlePointerDown = (event: React.PointerEvent<HTMLElement>) => {
        dragRef.current = {
            startAlong: along(event),
            startAcross: across(event),
            startedOpen: isOpen,
            moved: false,
            abandoned: false,
            fromControl: Boolean((event.target as HTMLElement).closest("button"))
        };

        // Pointer capture is claimed once a real drag begins (see handlePointerMove). Taking it here would
        // retarget the pointerup and stop the buttons in the container from ever seeing a click.
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLElement>) => {
        const drag = dragRef.current;

        if (!drag || drag.abandoned) {
            return;
        }

        const travelAlong = along(event) - drag.startAlong;
        const travelAcross = across(event) - drag.startAcross;

        if (!drag.moved) {
            // The cross axis counts towards the tolerance only where it can win the gesture outright,
            // below.
            const isWobble = Math.abs(travelAlong) <= TAP_TOLERANCE_PX
                && !(yieldsToCrossAxisScroll && Math.abs(travelAcross) > TAP_TOLERANCE_PX);

            if (isWobble) return;

            // Where something scrolls inside the container that slides, a mostly cross-axis gesture has to
            // be left to it.
            if (yieldsToCrossAxisScroll && Math.abs(travelAlong) <= Math.abs(travelAcross)) {
                drag.abandoned = true;
                return;
            }

            drag.moved = true;
            event.currentTarget.setPointerCapture(event.pointerId);
        }

        // The offset picks up from wherever the container was resting, and stops at either end: dragging
        // on past open or closed must not pull the container off its rails.
        const offsetAtDragStart = drag.startedOpen ? 0 : closedOffset;
        const draggedOffset = offsetAtDragStart + travelAlong;

        setDragOffset(Math.min(closedOffset, Math.max(0, draggedOffset)));
    };

    const handlePointerUp = (event: React.PointerEvent<HTMLElement>) => {
        const drag = dragRef.current;
        dragRef.current = null;
        setDragOffset(null);

        if (!drag) return;

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }

        if (!drag.moved) {
            // Without an onTap the tap is left to whatever was tapped, which is also how a keyboard
            // reaches those controls.
            onTap?.({startedOpen: drag.startedOpen, fromControl: drag.fromControl});
            return;
        }

        if (swallowClickAfterDrag) {
            wasDragged.current = true;
        }

        // A real drag settles to whichever end it was heading for. Travel counts towards the end the
        // container was not resting at, which is the closing direction when it started open and the
        // opening direction when it started closed.
        const travelAlong = along(event) - drag.startAlong;
        const travelTowardsOtherEnd = drag.startedOpen ? travelAlong : -travelAlong;

        setIsOpen(travelTowardsOtherEnd >= commitDistance ? !drag.startedOpen : drag.startedOpen);
    };

    const handleClickCapture = (event: React.MouseEvent<HTMLElement>) => {
        if (!wasDragged.current) return;

        wasDragged.current = false;
        event.stopPropagation();
        event.preventDefault();
    };

    return {
        offset: dragOffset ?? (isOpen ? 0 : closedOffset),
        isDragging: dragOffset !== null,
        handlers: {
            onPointerDown: handlePointerDown,
            onPointerMove: handlePointerMove,
            onPointerUp: handlePointerUp,
            onPointerCancel: handlePointerUp,
            ...(swallowClickAfterDrag ? {onClickCapture: handleClickCapture} : {})
        }
    };
}
